'use client';

import {
  AudioLines,
  Hand,
  MessageCircle,
  Mic,
  MicOff,
  PhoneOff,
} from 'lucide-react';
import Link from 'next/link';
import { motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { useId, type KeyboardEvent, type ReactNode } from 'react';

import { HoldToSpeakButton } from '@/components/voice/hold-to-speak-button';
import { useFloatingVoiceDockPosition } from '@/components/voice/use-floating-voice-dock-position';
import { VoiceCompanionAvatar } from '@/components/voice/voice-companion-avatar';
import { VoiceToolActivityBubble } from '@/components/voice/voice-tool-activity-bubble';
import type { VoiceToolActivity } from '@/lib/voice-tool-activity';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getDocumentDirection } from '@/i18n/config';
import { getFloatingVoiceDockCopy } from '@/i18n/floating-voice-dock-copy';
import {
  getFloatingVoiceCaptionMotion,
  getFloatingVoiceToolActivity,
  getVoiceDockStatusKey,
  type FloatingVoiceDockStatus,
} from '@/lib/floating-voice-dock-state';
import { cn } from '@/lib/utils';
import { formatVoiceCallDuration } from '@/lib/voice-call-ui';

export type FloatingVoiceDockProps = {
  locale: string;
  status: FloatingVoiceDockStatus;
  toolActivity?: VoiceToolActivity | null;
  isEnding?: boolean;
  inputMode: 'vad' | 'ptt';
  micMuted: boolean;
  pttActive: boolean;
  canPressToSpeak: boolean;
  isSourceChatVisible?: boolean;
  sourceThreadHref: string;
  sourceThreadTitle?: string;
  callElapsedMs: number;
  errorText?: string | null;
  onToggleMute: () => void;
  onInputModeChange: (mode: 'vad' | 'ptt') => void;
  onPressToSpeakStart: () => void;
  onPressToSpeakEnd: () => void;
  onEnd: () => void;
};

function PillTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={-1}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function FloatingVoiceDock(props: FloatingVoiceDockProps) {
  const moveHintId = useId();
  const isPresent = useIsPresent();
  const reducedMotion = useReducedMotion();
  const starting = props.status === 'connecting';
  const controlsDisabled = starting || Boolean(props.isEnding) || !isPresent;
  const {
    boundaryRef,
    panelRef,
    position,
    point,
    ready,
    canAnimatePosition,
    dragging,
    onDragPointerDown,
    onDragPointerMove,
    onDragPointerUp,
    moveByKey,
  } = useFloatingVoiceDockPosition(Boolean(props.isSourceChatVisible));
  const copy = getFloatingVoiceDockCopy(props.locale);
  const direction = getDocumentDirection(props.locale);
  const statusKey = getVoiceDockStatusKey(
    props.status,
    props.micMuted,
    props.pttActive,
    props.inputMode
  );
  const statusText =
    (props.isEnding ? copy.endingCall : props.errorText) ||
    (statusKey === 'holdSpace'
      ? copy.ready
      : statusKey === 'releaseToSend'
        ? copy.listening
        : copy[statusKey]);
  const canPress =
    props.canPressToSpeak &&
    !controlsDisabled &&
    !props.micMuted &&
    props.status !== 'connecting' &&
    props.status !== 'error' &&
    props.status !== 'idle';
  const nextMode = props.inputMode === 'vad' ? 'ptt' : 'vad';
  const modeActionLabel =
    props.inputMode === 'vad' ? copy.switchToPushToTalk : copy.pushToTalkOn;
  const duration = formatVoiceCallDuration(props.callElapsedMs);

  const switchMode = () => {
    props.onInputModeChange(nextMode);
  };

  const onAvatarKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key !== 'ArrowUp' &&
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return;
    event.preventDefault();
    moveByKey(event.key);
  };

  return (
    <div
      ref={boundaryRef}
      data-slot="floating-voice-boundary"
      className="sidebar-offset-transition pointer-events-none fixed inset-y-0 right-0 left-0 z-40 lg:left-[var(--sidebar-width,0px)]"
    >
      <section
        ref={panelRef}
        data-slot="floating-voice-dock"
        data-dock-corner={position}
        dir={direction}
        role="region"
        aria-label={copy.activeCall}
        aria-busy={starting || props.isEnding}
        className={cn(
          'pointer-events-none absolute top-0 left-0 flex w-36 max-w-[calc(100vw-1.5rem)] flex-col items-center pt-4',
          canAnimatePosition && !dragging
            ? 'transition-transform duration-200 ease-out motion-reduce:transition-none'
            : 'transition-none',
          ready ? 'visible' : 'invisible'
        )}
        style={{ transform: `translate3d(${point.x}px, ${point.y}px, 0)` }}
      >
        <motion.div
          className="relative isolate flex w-full origin-bottom flex-col items-center [filter:drop-shadow(0_0_4px_var(--background))_drop-shadow(0_0_8px_var(--background))_drop-shadow(0_0_12px_var(--background))]"
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: reducedMotion ? 0 : 0.5,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <span id={moveHintId} className="sr-only">
            {copy.moveHint}
          </span>
          <div className="pointer-events-auto relative z-20 mb-1 flex size-16 shrink-0 items-center justify-center">
            <VoiceToolActivityBubble
              locale={props.locale}
              activity={getFloatingVoiceToolActivity({
                status: props.status,
                activity: props.toolActivity,
                isEnding: props.isEnding,
                isPresent,
              })}
            />
            <button
              type="button"
              data-slot="floating-voice-avatar"
              aria-label={copy.moveHint}
              aria-describedby={moveHintId}
              title={copy.moveHint}
              disabled={Boolean(props.isEnding) || !isPresent}
              className={cn(
                'relative flex size-16 touch-none items-center justify-center rounded-full',
                'focus-visible:ring-primary/25 cursor-grab outline-none select-none focus-visible:ring-4 active:cursor-grabbing'
              )}
              onPointerDown={onDragPointerDown}
              onPointerMove={onDragPointerMove}
              onPointerUp={onDragPointerUp}
              onPointerCancel={onDragPointerUp}
              onKeyDown={onAvatarKeyDown}
            >
              <VoiceCompanionAvatar
                status={props.status}
                inputMode={props.inputMode}
                micMuted={props.micMuted}
                pttActive={props.pttActive}
              />
            </button>
            <PillTooltip label={copy.openChat}>
              <Link
                data-slot="floating-voice-chat-link"
                href={props.sourceThreadHref}
                title={props.sourceThreadTitle?.trim() || copy.voiceChat}
                aria-label={copy.openChat}
                className={cn(
                  'text-muted-foreground absolute -right-3 -bottom-1 z-10 flex size-6 items-center justify-center rounded-full',
                  'hover:text-foreground focus-visible:ring-primary transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none'
                )}
              >
                <span className="border-border bg-card hover:bg-accent flex size-5 items-center justify-center rounded-full border shadow-sm transition-colors duration-150">
                  <MessageCircle className="size-2.5" aria-hidden />
                </span>
              </Link>
            </PillTooltip>
          </div>

          <div
            data-slot="floating-voice-surface"
            className="border-border/80 bg-card/95 pointer-events-auto relative z-20 flex h-[2.625rem] shrink-0 items-center gap-0.5 rounded-full border px-1 shadow-lg backdrop-blur-md"
          >
            {props.inputMode === 'vad' || props.micMuted ? (
              <PillTooltip
                label={props.micMuted ? copy.unmuteMic : copy.muteMic}
              >
                <Button
                  type="button"
                  data-slot="floating-voice-mute"
                  size="icon"
                  variant="ghost"
                  className={cn(
                    'size-8 rounded-full text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-700 dark:text-emerald-300',
                    props.micMuted &&
                      'bg-red-500/10 text-red-600 hover:bg-red-500/15 hover:text-red-600'
                  )}
                  aria-label={props.micMuted ? copy.unmuteMic : copy.muteMic}
                  aria-pressed={props.micMuted}
                  disabled={controlsDisabled}
                  onClick={props.onToggleMute}
                >
                  {props.micMuted ? (
                    <MicOff className="size-3.5" aria-hidden />
                  ) : (
                    <Mic className="size-3.5" aria-hidden />
                  )}
                </Button>
              </PillTooltip>
            ) : (
              <PillTooltip
                label={props.pttActive ? copy.releaseToSend : copy.holdToSpeak}
              >
                <HoldToSpeakButton
                  disabled={!canPress}
                  active={props.pttActive}
                  label={copy.holdToSpeak}
                  activeLabel={copy.releaseToSend}
                  onStart={props.onPressToSpeakStart}
                  onEnd={props.onPressToSpeakEnd}
                />
              </PillTooltip>
            )}
            <PillTooltip label={modeActionLabel}>
              <Button
                type="button"
                data-slot="floating-voice-mode-switch"
                size="icon"
                variant="ghost"
                className="size-8 rounded-full bg-violet-500/10 text-violet-700 hover:bg-violet-500/15 hover:text-violet-700 dark:text-violet-300"
                aria-label={modeActionLabel}
                disabled={controlsDisabled}
                onClick={switchMode}
              >
                {starting ? (
                  <Loader className="size-3.5" aria-label={copy.connecting} />
                ) : props.inputMode === 'vad' ? (
                  <AudioLines className="size-3.5" aria-hidden />
                ) : (
                  <Hand className="size-3.5" aria-hidden />
                )}
              </Button>
            </PillTooltip>
            <PillTooltip
              label={props.isEnding ? copy.endingCall : copy.endCall}
            >
              <Button
                type="button"
                data-slot="floating-voice-end"
                size="icon"
                variant="ghost"
                className="ms-0.5 size-8 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/15 hover:text-red-600"
                onClick={props.onEnd}
                disabled={Boolean(props.isEnding) || !isPresent}
                aria-label={props.isEnding ? copy.endingCall : copy.endCall}
              >
                {props.isEnding ? (
                  <Loader className="size-3.5" aria-label={copy.endingCall} />
                ) : (
                  <PhoneOff className="size-3.5" aria-hidden />
                )}
              </Button>
            </PillTooltip>
          </div>

          <motion.div
            data-slot="floating-voice-caption"
            className="text-foreground pointer-events-none relative isolate z-10 mt-2 flex w-full flex-col items-center gap-0.5 text-center text-xs leading-4"
            initial={false}
            animate={getFloatingVoiceCaptionMotion(
              dragging,
              Boolean(reducedMotion)
            )}
            transition={{
              duration: reducedMotion ? 0 : 0.24,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <span
              aria-hidden="true"
              className="bg-background/95 absolute -inset-x-1 top-0 -bottom-3 -z-10 [mask-image:radial-gradient(ellipse_at_center,black_65%,transparent_100%)] backdrop-blur-md"
            />
            <div className="flex w-full min-w-0 items-center justify-center">
              <span
                className="min-w-0 truncate font-medium"
                role="status"
                aria-live="polite"
              >
                {statusText}
              </span>
              <span className="shrink-0 px-1.5" aria-hidden>
                ·
              </span>
              <span
                className="shrink-0 tabular-nums"
                aria-label={copy.callTime.replace('{duration}', duration)}
              >
                {duration}
              </span>
            </div>
            <span
              data-slot="floating-voice-mode-hint"
              className="w-full font-medium break-words text-violet-700 dark:text-violet-300"
            >
              {props.inputMode === 'vad' ? copy.auto : copy.pushToTalk}
            </span>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
